import { AddingVisitorsBlockContainer, StyledButton } from './styles';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { VisitorBlock } from './components/visitor-block';
import { selectAllVisitorsEmails } from 'store/visitors-emails/selectors';
import { selectResource } from 'store/resource/selectors';
import { setVisitors, removeVisitor } from 'store/visitors-emails/actions';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/pro-regular-svg-icons';
import { Visitor, VisitorsEmailsState } from 'store/visitors-emails/types';
import { useAddingVisitorsBlock } from './hooks/use-adding-visitors-block.hook';

export const AddingVisitorsBlock: React.FC = (): JSX.Element => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const { allowed_visitors: allowedVisitors } = useSelector(selectResource) || {};
  const visitors: VisitorsEmailsState = useSelector(selectAllVisitorsEmails);
  const [visibleVisitors, setVisibleVisitors] = useState<number>(0);
  const { errorConfig, markFieldAsTouched } = useAddingVisitorsBlock();

  const onChangeVisitors = useCallback(
    (key: number, value: Visitor) => {
      dispatch(setVisitors({ ...visitors, [key]: value } as VisitorsEmailsState));
    },
    [dispatch, visitors],
  );

  const handleFieldBlur = useCallback(
    (visitorNumber: number, fieldName: 'firstName' | 'lastName' | 'email') => {
      markFieldAsTouched(visitorNumber, fieldName);
    },
    [markFieldAsTouched],
  );

  const handleAddGuest = useCallback(() => {
    if (visibleVisitors < allowedVisitors) {
      setVisibleVisitors(prev => prev + 1);
    }
  }, [visibleVisitors, allowedVisitors]);

  const handleDeleteGuest = useCallback(
    (visitorNumber: number) => {
      dispatch(removeVisitor(visitorNumber));
      setVisibleVisitors(prev => prev - 1);
    },
    [dispatch],
  );

  const canAddMoreGuests = visibleVisitors < allowedVisitors;

  return (
    <AddingVisitorsBlockContainer data-testid="adding-visitors-block" $hasVisibleVisitors={visibleVisitors > 0}>
      {Array.from({ length: visibleVisitors }, (_, index) => index + 1).map(visitorNumber => (
        <VisitorBlock
          errorConfig={errorConfig.visitorErrors[visitorNumber]}
          touchedConfig={errorConfig.visitorTouched[visitorNumber]}
          key={visitorNumber}
          value={visitors?.[visitorNumber]}
          onChangeVisitors={onChangeVisitors}
          visitorNumber={visitorNumber}
          onFieldBlur={fieldName => handleFieldBlur(visitorNumber, fieldName)}
          onDeleteGuest={handleDeleteGuest}
        />
      ))}
      {canAddMoreGuests && (
        <StyledButton
          variant="link"
          onClick={handleAddGuest}
          data-testid="add-guest-button"
          disabled={!canAddMoreGuests}
          text={intl.formatMessage({ id: 'add_guest' })}
          Icon={<FontAwesomeIcon icon={faPlus} size="lg" />}
          iconDirection="left"
        />
      )}
    </AddingVisitorsBlockContainer>
  );
};
